import { useEffect, useRef } from "react";
import JVConversations from "./JVConversations";
import JVChat from "./JVChat";
import { IConversation, IMessage, IUser } from "./modal";
import { Fetch, hrLenseBaseUrl } from "../../shared/Fetch";
import { Dialog } from "primereact/dialog";
import { useNotification } from "../../shared/NotificatonContext";
import { Toast } from "primereact/toast";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import profilePic from "../../assets/Images/profile_pic_placeholder.jpg";

export default function JVConnect() {
    const myId = JSON.parse(sessionStorage.getItem("auth") ?? "").id;
    const { connection, getUserById, setUsersFromArray, updateOnlineUsers,
        open, setOpen, setReq, conversations, setConversations, currentConversationId, setCurrentConversationId, onUserSelect
    } = useNotification();
    const toast = useRef<any>(null);

    const unreadCount = conversations?.reduce((count, u) => { return count += u.unreadCount }, 0) ?? 0;

    const currentConversationRef = useRef<number | undefined>();
    const openRef = useRef<boolean>(false);
    useEffect(() => {
        currentConversationRef.current = currentConversationId;
    }, [currentConversationId]);
    useEffect(() => {
        openRef.current = open;
    }, [open]);

    useEffect(() => {
        if (currentConversationId && open) {
            const currentConversation = conversations?.find(c => c.id === currentConversationId);
            const messsages = currentConversation?.messages;
            if (!messsages || messsages.length === 0) loadChatHistory(currentConversationId);
            else if (currentConversation.unreadCount > 0) markReadConversation(currentConversationId);
        }
    }, [currentConversationId, open]);

    async function loadUsers() {
        const res = await Fetch('Experiment/jv-users', 'GET', null, null, null);
        if (res) {
            const data: IUser[] = await res.json();
            setUsersFromArray(data);
            await loadConversations(data);
        }
    }

    async function loadConversations(users: IUser[]) {
        const res = await Fetch('Experiment/my-conversations', 'GET', null, null, null);
        if (res) {
            const data: IConversation[] = await res.json();
            const modify = data.map(c => {
                c.user = users.find(u => u.id === c.userId)!;
                return c;
            });
            setConversations(modify);
        }
    }

    async function startConversation(userId: number) {
        const res = await Fetch('Experiment/start-conversation', 'POST', null, `userId=${userId}`, null);
        if (res) {
            const data: IConversation = await res.json();
            setConversations(prev => {
                const updated = prev?.map(conv =>
                    conv.userId === data.userId ? {
                        ...data,
                        user: getUserById(data.userId!)
                    } : conv
                );
                setCurrentConversationId(data.id);
                return updated;
            });
        }
    }

    useEffect(() => { loadUsers(); }, []);

    useEffect(() => {
        if (!connection) return;

        connection.on("OnlineUsers", (users: number[]) => {
            updateOnlineUsers('set', users);
        });

        connection.on("UserOnline", (userId: number) => {
            updateOnlineUsers('add', userId);
        });

        connection.on("UserOffline", (userId: number) => {
            updateOnlineUsers('remove', userId);
        });

        connection.on("ReceiveMessage", (message: IMessage) => {
            message.sender = getUserById(message.senderId)!;
            setConversations(prev => {
                const existing = prev?.find(f => f.id === message.conversationId);
                if (existing) {
                    const updatedConversation = {
                        ...existing,
                        lastMessage: message.fileName ?? message.message,
                        lastMessageTime: message.createdAt,
                        messages: existing.messages ? [...existing.messages, message] : existing.messages,
                        unreadCount: (!openRef.current ||
                            (message.conversationId !== currentConversationRef.current && message.senderId !== myId))
                            ? existing.unreadCount + 1 : existing.unreadCount
                    };

                    const filtered = prev ? prev.filter(f => f.id !== message.conversationId) : [];
                    return [updatedConversation, ...filtered];
                } else {
                    const newConversation: IConversation = {
                        id: message.conversationId,
                        lastMessage: message.fileName ?? message.message,
                        lastMessageTime: message.createdAt,
                        user: getUserById(message.senderId)!,
                        userId: message.senderId,
                        messages: [message],
                        unreadCount: 1,
                        isGroup: false,
                    }
                    const filtered = prev ?? [];
                    return [newConversation, ...filtered];
                }
            });

            if (!openRef.current ||
                (message.conversationId !== currentConversationRef.current && message.senderId !== myId)) {
                const user = getUserById(message.senderId);
                toast.current.clear();
                toast.current.show({
                    severity: 'info',
                    summary: message.fileName ?? message.message,
                    life: 5000,
                    content: (props: any) => (
                        <div className="flex flex-column align-items-left" style={{ flex: '1' }}>
                            <div className="flex align-items-center gap-2">
                                <div style={styles.avatar}>
                                    {user?.employeeId
                                        ? <img src={`${hrLenseBaseUrl}/Hrlense_Employee/getEmployeeImages?EmployeeCode=${user.employeeId}`}
                                            onError={e => e.currentTarget.src = profilePic}
                                            style={styles.avatarImg} />
                                        : user?.name.charAt(0).toUpperCase()}
                                </div>
                                <span className="font-bold text-900">{user?.name}</span>
                            </div>
                            <div style={styles.notificationMessage} className="font-medium text-lg my-3 text-900">{props.message.summary}</div>
                            <div>
                                <Button className="p-button-sm" label="Reply" onClick={() => {
                                    setCurrentConversationId(message.conversationId);
                                    setOpen(true);
                                }}></Button></div>
                        </div>
                    )
                });
            }
        });
        return () => {
            connection.off("OnlineUsers");
            connection.off("UserOnline");
            connection.off("UserOffline");
            connection.off("ReceiveMessage");
        };
    }, [connection]);

    async function loadChatHistory(conversationId: number) {
        if (!conversationId) return;
        const res = await Fetch('Experiment/chat-history', 'GET', null, `conversationId=${conversationId}`, null);
        if (res) {
            const data: IMessage[] = await res.json();
            const modify = data.map(c => {
                c.sender = getUserById(c.senderId)!;
                return c;
            });
            setConversations(prev => {
                return prev?.map(conv =>
                    conv.id === conversationId ?
                        { ...conv, messages: modify }
                        : conv
                );
            });
            const currentConversation = conversations?.find(f => f.id === currentConversationId);
            if (currentConversation && currentConversation.unreadCount > 0) {
                markReadConversation(conversationId);
            }
        }
    }

    async function markReadConversation(conversationId: number) {
        if (!conversationId) return;
        const res = await Fetch('Experiment/mark-as-read', 'POST', null, `conversationId=${conversationId}`, null);
        if (res) {
            setConversations(prev => {
                return prev?.map(conv =>
                    conv.id === currentConversationId ? { ...conv, unreadCount: 0 } : conv
                );
            });
        };
    }

    const header = () => {
        return (
            <div style={{ display: 'flex', }}>
                <h4>Chat</h4>
                {unreadCount > 0 && <Badge style={{ marginLeft: 2 }} value={unreadCount} />}
            </div>
        );
    }

    return (
        <>
            <button type="button" className="p-link" onClick={() => setOpen(true)}>
                <i className="pi pi-comments"></i>
                {unreadCount > 0 && <span className="topbar-badge">{unreadCount}</span>}
            </button>
            <Dialog visible={open} header={header} headerStyle={{ paddingBottom: 0 }} style={{ width: '100vw', maxWidth: '1400px', height: '95vh' }}
                className="p-fluid" onHide={() => { setOpen(false); setReq(undefined) }}>
                <div style={styles.container}>
                    <JVConversations
                        conversations={conversations}
                        currentConversationId={currentConversationId}
                        onSelectConversationId={setCurrentConversationId}
                        onUserSelect={onUserSelect}
                    />
                    {conversations?.find(f => f.id === currentConversationId) ?
                        <JVChat
                            myId={myId}
                            conversation={conversations.find(f => f.id === currentConversationId)!}
                            startConversation={startConversation}
                        /> : <h4 className="p-4">Select user to chat with</h4>
                    }
                </div>
            </Dialog>
            <Toast ref={toast} />
        </>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: "flex",
        height: "100%",
        width: '100%',
        backgroundColor: "#f5f7fb",
        fontFamily: "Segoe UI"
    },
    avatar: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        backgroundColor: "#4e73df",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        position: 'relative',
    },
    avatarImg: {
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        objectFit: "cover"
    },
    notificationMessage: {
        display: "-webkit-box",
        WebkitLineClamp: 5,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        fontSize: 28,
    }
};